import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { ratioToPercent, ratioToPercentPlus } from '../../../lib/format';
import { toFixedDecimalString } from '../../../shared/chart-options';
import PlayersLineup from '../../shared/PlayersLineup';
import ItemSelector from '../../shared/ItemSelector';
import {
  STAT_TYPE_SCORING,
  STAT_TYPE_FOUR_FACTORS,
  STAT_TYPE_BOXSCORE,
} from '../../../shared/constants';
import { title } from '../../AdvancedStats/translations';

const classNames = require('classnames');
const PARTIAL_BOXSCORE_FIELDS = ['ast', 'stl', 'blk', 'to', 'fouls', 'pts'];

const LineupInfoModal = ({
  shift,
  pathTo,
  isVisible,
  shifts,
  isCurrentTeam,
  closeDialog,
  hasPrev,
  hasNext,
  goPrevShift,
  goNextShift,
}) => {
  const [statType, setStatType] = useState(STAT_TYPE_SCORING);

  if (!shift) return null;

  const teamColor = shift.teamColor;
  const combinedStatClasses = classNames({
    'score-table__team-stat': true,
    'score-table__combined-stat': true,
  });

  // TODO: This needs to go in a js file elsewhere, it will share this with lineup explorer
  const statTypes = [
    {
      id: STAT_TYPE_SCORING,
      name: 'Scoring',
    },
    {
      id: STAT_TYPE_FOUR_FACTORS,
      name: 'Four Factors',
    },
    {
      id: STAT_TYPE_BOXSCORE,
      name: 'Box Score',
    },
  ];

  const changeStatType = statType => {
    setStatType(statType);
  };

  const getScoringTable = shift => {
    return (
      <tbody className="score-table__categories-values">
        <tr>
          <th>Points Scored</th>
          <td className="score-table__team-stat">
            {shift.stats.shift.home.points}
          </td>
          <td>{shift.stats.shift.visitor.points}</td>
          <td className="score-table__team-stat">
            {shift.stats.game.home.points}
          </td>
          <td>{shift.stats.game.visitor.points}</td>
        </tr>
        <tr>
          <th>Points per Minute</th>
          <td className="score-table__team-stat">
            {toFixedDecimalString(shift.stats.shift.home.pointsPerMinute)}
          </td>
          <td>
            {toFixedDecimalString(shift.stats.shift.visitor.pointsPerMinute)}
          </td>
          <td className="score-table__team-stat">
            {toFixedDecimalString(shift.stats.game.home.pointsPerMinute)}
          </td>
          <td>
            {toFixedDecimalString(shift.stats.game.visitor.pointsPerMinute)}
          </td>
        </tr>
        <tr>
          <th>Points per Possession</th>
          <td className="score-table__team-stat">
            {toFixedDecimalString(shift.stats.shift.home.pointsPerPossession)}
          </td>
          <td>
            {toFixedDecimalString(
              shift.stats.shift.visitor.pointsPerPossession,
            )}
          </td>
          <td className="score-table__team-stat">
            {toFixedDecimalString(shift.stats.game.home.pointsPerPossession)}
          </td>
          <td>
            {toFixedDecimalString(shift.stats.game.visitor.pointsPerPossession)}
          </td>
        </tr>
        <tr>
          <th>Possessions</th>
          <td colSpan="2" className={combinedStatClasses}>
            {toFixedDecimalString(
              (shift.calculated.possessions +
                shift.opp_calculated.possessions) /
                2,
            )}
          </td>
          <td colSpan="2" className={combinedStatClasses}>
            {toFixedDecimalString(
              (shift.lineup.calculated.possessions +
                shift.lineup.opp_calculated.possessions) /
                2,
            )}
          </td>
        </tr>
        <tr>
          <th>Plus-Minus Points</th>
          <td colSpan="2" className={combinedStatClasses}>
            {shift.stats.shift.home.plusMinus}
          </td>
          <td colSpan="2" className={combinedStatClasses}>
            {shift.stats.game.home.plusMinus}
          </td>
        </tr>
      </tbody>
    );
  };

  const getFourFactorsTable = shift => {
    const totalTeamFourfactors = shift.lineup.calculated.four_factors;
    const totalOppFourfactors = shift.lineup.opp_calculated.four_factors;

    const shiftTeamFourfactors = shift.calculated.four_factors;
    const shiftOppFourfactors = shift.opp_calculated.four_factors;
    return (
      <tbody className="score-table__categories-values">
        <tr>
          <th>Shooting (EFG%)</th>
          <td className="score-table__team-stat">
            {ratioToPercent(shiftTeamFourfactors.efg)}
          </td>
          <td>{ratioToPercent(shiftOppFourfactors.efg)}</td>
          <td className="score-table__team-stat">
            {ratioToPercent(totalTeamFourfactors.efg)}
          </td>
          <td>{ratioToPercent(totalOppFourfactors.efg)}</td>
        </tr>
        <tr>
          <th>Rebounding (ORR)</th>
          <td className="score-table__team-stat">
            {ratioToPercent(shiftTeamFourfactors.orr)}
          </td>
          <td>{ratioToPercent(shiftOppFourfactors.orr)}</td>
          <td className="score-table__team-stat">
            {ratioToPercent(totalTeamFourfactors.orr)}
          </td>
          <td>{ratioToPercent(totalOppFourfactors.orr)}</td>
        </tr>
        <tr>
          <th>Turnovers (TOV%)</th>
          <td className="score-table__team-stat">
            {ratioToPercent(shiftTeamFourfactors.tov)}
          </td>
          <td>{ratioToPercent(shiftOppFourfactors.tov)}</td>
          <td className="score-table__team-stat">
            {ratioToPercent(totalTeamFourfactors.tov)}
          </td>
          <td>{ratioToPercent(totalOppFourfactors.tov)}</td>
        </tr>
        <tr>
          <th>Free Throws (FTR)</th>
          <td className="score-table__team-stat">
            {ratioToPercent(shiftTeamFourfactors.ftr)}
          </td>
          <td>{ratioToPercent(shiftOppFourfactors.ftr)}</td>
          <td className="score-table__team-stat">
            {ratioToPercent(totalTeamFourfactors.ftr)}
          </td>
          <td>{ratioToPercent(totalOppFourfactors.ftr)}</td>
        </tr>

        <tr>
          <th>Possessions</th>
          <td colSpan="2" className={combinedStatClasses}>
            {toFixedDecimalString(
              (shift.calculated.possessions +
                shift.opp_calculated.possessions) /
                2,
            )}
          </td>
          <td colSpan="2" className={combinedStatClasses}>
            {toFixedDecimalString(
              (shift.lineup.calculated.possessions +
                shift.lineup.opp_calculated.possessions) /
                2,
            )}
          </td>
        </tr>
      </tbody>
    );
  };

  const getBoxscoreTable = shift => {
    const boxscore = shift.boxscore;
    const oppBoxscore = shift.opp_boxscore;
    const totalBoxscore = shift.lineup.total.boxscore;
    const totalOppBoxscore = shift.lineup.total.opp_boxscore;

    return (
      <tbody className="score-table__categories-values">
        <tr>
          <th>Field Goals</th>
          <td className="score-table__team-stat">
            {boxscore.fgm}/{boxscore.fga}{' '}
            {boxscore.fga !== 0
              ? ratioToPercentPlus(boxscore.fgm / boxscore.fga, 1)
              : '-'}
          </td>
          <td>
            {oppBoxscore.fgm}/{oppBoxscore.fga}{' '}
            {oppBoxscore.fga !== 0
              ? ratioToPercentPlus(oppBoxscore.fgm / oppBoxscore.fga, 1)
              : '-'}
          </td>
          <td className="score-table__team-stat">
            {totalBoxscore.fgm}/{totalBoxscore.fga}{' '}
            {boxscore.fga !== 0
              ? ratioToPercentPlus(totalBoxscore.fgm / totalBoxscore.fga, 1)
              : '-'}
          </td>
          <td>
            {totalOppBoxscore.fgm}/{totalOppBoxscore.fga}{' '}
            {oppBoxscore.fga !== 0
              ? ratioToPercentPlus(
                  totalOppBoxscore.fgm / totalOppBoxscore.fga,
                  1,
                )
              : '-'}
          </td>
        </tr>
        <tr>
          <th>3PT</th>
          <td className="score-table__team-stat">
            {boxscore.fgm3}/{boxscore.fga3}{' '}
            {boxscore.fga3 !== 0
              ? ratioToPercentPlus(boxscore.fgm3 / boxscore.fga3, 1)
              : '-'}
          </td>
          <td>
            {oppBoxscore.fgm3}/{oppBoxscore.fga3} (
            {oppBoxscore.fga3 !== 0
              ? ratioToPercentPlus(oppBoxscore.fgm3 / oppBoxscore.fga3, 1)
              : '-'}
            )
          </td>
          <td className="score-table__team-stat">
            {totalBoxscore.fgm3}/{totalBoxscore.fga3} (
            {boxscore.fga3 !== 0
              ? ratioToPercentPlus(totalBoxscore.fgm3 / totalBoxscore.fga3, 1)
              : '-'}
            )
          </td>
          <td>
            {totalOppBoxscore.fgm3}/{totalOppBoxscore.fga3} (
            {oppBoxscore.fga3 !== 0
              ? ratioToPercentPlus(
                  totalOppBoxscore.fgm3 / totalOppBoxscore.fga3,
                  1,
                )
              : '-'}
            )
          </td>
        </tr>
        <tr>
          <th>Free Throws</th>
          <td className="score-table__team-stat">
            {boxscore.ft}/{boxscore.fta} (
            {boxscore.fta !== 0
              ? ratioToPercentPlus(boxscore.ft / boxscore.fta, 1)
              : '-'}
            )
          </td>
          <td>
            {oppBoxscore.ft}/{oppBoxscore.fta} (
            {oppBoxscore.fta !== 0
              ? ratioToPercentPlus(oppBoxscore.ft / oppBoxscore.fta, 1)
              : '-'}
            )
          </td>
          <td className="score-table__team-stat">
            {totalBoxscore.ft}/{totalBoxscore.fta} (
            {totalBoxscore.fta !== 0
              ? ratioToPercentPlus(totalBoxscore.ft / totalBoxscore.fta, 1)
              : '-'}
            )
          </td>
          <td>
            {totalOppBoxscore.ft}/{totalOppBoxscore.fta} (
            {totalOppBoxscore.fta !== 0
              ? ratioToPercentPlus(
                  totalOppBoxscore.ft / totalOppBoxscore.fta,
                  1,
                )
              : '-'}
            )
          </td>
        </tr>
        <tr>
          <th>Rebounds</th>
          <td className="score-table__team-stat">
            {boxscore.rebs} (O: {boxscore.rebso} D: {boxscore.rebsd})
          </td>
          <td>
            {oppBoxscore.rebs} (O: {oppBoxscore.rebso} D: {oppBoxscore.rebsd})
          </td>
          <td className="score-table__team-stat">
            {totalBoxscore.rebs} (O: {totalBoxscore.rebso} D:{' '}
            {totalBoxscore.rebsd})
          </td>
          <td>
            {totalOppBoxscore.rebs} (O: {totalOppBoxscore.rebso} D:{' '}
            {totalOppBoxscore.rebsd})
          </td>
        </tr>
        {PARTIAL_BOXSCORE_FIELDS.map(field => (
          <tr key={field}>
            <th>{title(field)}</th>
            <td className="score-table__team-stat">{boxscore[field]}</td>
            <td>{oppBoxscore[field]}</td>
            <td className="score-table__team-stat">{totalBoxscore[field]}</td>
            <td>{totalOppBoxscore[field]}</td>
          </tr>
        ))}
      </tbody>
    );
  };

  return (
    <Modal
      show={isVisible}
      onHide={closeDialog}
      size="lg"
      centered
      dialogClassName="s-game-explorer__modal"
    >
      <Modal.Header closeButton closeLabel="">
        <Modal.Title as="h5">Lineup Information</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="s-game-explorer__modal-navigation">
          <div className="action-bar">
            <div>
              <button
                className="btn-info"
                onClick={goPrevShift}
                disabled={!hasPrev}
              >
                Previous
              </button>
              <button
                className="btn-info"
                onClick={goNextShift}
                disabled={!hasNext}
              >
                Next
              </button>
            </div>
            <div>
              <ItemSelector
                className="s-game-explorer__stat-selector"
                items={statTypes}
                value={statType}
                onChange={changeStatType}
              />
            </div>
          </div>
        </div>

        <h6 className="s-game-explorer__modal-title">
          <PlayersLineup
            players={shift.lineup.players}
            style={{ color: teamColor }}
          />
        </h6>

        <div className="s-game-explorer__modal-table-container">
          <table className="s-game-explorer__modal-table">
            <thead>
              <tr>
                <th />
                <th colSpan="2" className="s-game-explorer__modal-type">
                  Shift
                </th>
                <th
                  colSpan="2"
                  rowSpan="2"
                  className="s-game-explorer__modal-type"
                >
                  Total Game
                </th>
              </tr>
              <tr>
                <th />
                <th colSpan="2" className="s-game-explorer__modal-type">
                  {shift.stats.shift.startTime} - {shift.stats.shift.endTime}
                </th>
              </tr>
              <tr>
                <th />
                <th
                  colSpan="2"
                  title={'Played Time: ' + shift.stats.shift.time}
                >
                  {shift.stats.shift.time}
                </th>
                <th colSpan="2" title={'Played Time: ' + shift.stats.game.time}>
                  {shift.stats.game.time}
                </th>
              </tr>
              <tr>
                <td />
                <th className="score-table__categories-title">Team</th>
                <th className="score-table__categories-title">Opponent</th>
                <th className="score-table__categories-title">Team</th>
                <th className="score-table__categories-title">Opponent</th>
              </tr>
            </thead>
            {statType === STAT_TYPE_SCORING && getScoringTable(shift)}
            {statType === STAT_TYPE_FOUR_FACTORS && getFourFactorsTable(shift)}
            {statType === STAT_TYPE_BOXSCORE && getBoxscoreTable(shift)}
          </table>

          {isCurrentTeam && (
            <Link
              to={pathTo('lineup-explorer', shift.lineup.lineup.id)}
              className="s-game-explorer__modal-lineup-explorer btn-info"
            >
              Lineup Explorer
            </Link>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LineupInfoModal;
